import { useState, useEffect, useCallback } from 'react';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { useFormContext } from '@context/FormContext';
import { Screens } from '@shared/constants/screens';
import { getMake, getModel, getYear } from '@services/enterLicensePlateManually';
import { formatOptions, IOption } from '@services/utils';
import { getMileages } from '@shared/constants/mileages';
import useForm, { validate } from '@shared/hooks/useForm';
import { CDX_ACTIONS, IManualLicensePlateInfoForm } from 'types/form';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';


type ManualLicensePlateForm = Omit<IManualLicensePlateInfoForm, 'makeName'>;
const VehicleManualEnterForm = () => {
  const { t } = useTexts();
  const { trackFormSubmit, trackPage, startInteraction, trackClick } = useAnalytics(SEGMENT_SCREEN_PARAMS.VEHICLE_SELECTION_MANUAL);

  const mileages = getMileages();
  const [years, setYears] = useState<IOption[]>([]);
  const [makes, setMakes] = useState<IOption[]>([]);
  const [models, setModels] = useState<IOption[]>([]);
  const { next } = useScreenNavigator();
  const { currentStep, updateManualLicensePlateInfoFormData, handleStep, manualLicensePlateInfoForm, dispatch } = useFormContext();

  const { errors, handleChange, handleSubmit } = useForm<ManualLicensePlateForm>({
    initialValues: {
      ...manualLicensePlateInfoForm,
    },
    validationSchema: {
      year: validate.string('year').required(t('vehicleManualEnter.validation.year')),
      make: validate.string('make').required(t('vehicleManualEnter.validation.make')),
      model: validate.string('model').required(t('vehicleManualEnter.validation.model')),
      estimatedMileage: validate.string('estimatedMileage').required(t('vehicleManualEnter.validation.estimatedMileage')),
      currentMonthlyPayment: validate
      .number('currentMonthlyPayment')
      .minNumber(1, t('vehicleManualEnter.validation.currentMonthlyPayment.required'))
      .isNumber(t('vehicleManualEnter.validation.currentMonthlyPayment.isNumber')),

    },
    onSubmit: async () => {
      const currentYear = new Date().getFullYear();
      if (
        Number(manualLicensePlateInfoForm.year) < currentYear - 10 ||
        manualLicensePlateInfoForm.estimatedMileage === 'Over 150,000 miles'
      ) {
        if (manualLicensePlateInfoForm.estimatedMileage === 'Over 150,000 miles') {
          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: false,
            qualified_fail_reason: 'estimated_mileage',
            ...manualLicensePlateInfoForm,
          });
        }
        if (Number(manualLicensePlateInfoForm.year) < currentYear - 10) {
          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: false,
            qualified_fail_reason: 'year',
            ...manualLicensePlateInfoForm,
          });
        }
        next(currentStep, 'preSubmitOutcome', {
          manualLicenseForm: manualLicensePlateInfoForm
        }, false);
        } else {
          trackFormSubmit('Form Submitted', {
            submission_status: true,
            submission_method: 'manual',
            forward_screen_name: 'prove_identity',
          });
          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: true,
            ...manualLicensePlateInfoForm,
          });
        }
        next(currentStep, 'postSubmitOutcome', {
          manualLicenseForm: manualLicensePlateInfoForm
        }, false);
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  useEffect(() => {
    getYears();
  }, []);

  useEffectOnce(() => trackPage(t('pageTitles.VehicleManualEnter')));

  const getYears = useCallback(async () => {
    const yearsResponse = await getYear();
    if ('error' in yearsResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: yearsResponse,
      });
    } else {
      const sortedYears = yearsResponse.sort((a, b) => Number(b.modelYear) - Number(a.modelYear));
      setYears(formatOptions(sortedYears, 'modelYear', 'modelYear'));
    }
  }, []);

  const getMakes = async (year: string) => {
    const makeResponse = await getMake(year);
    if ('error' in makeResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: makeResponse,
      });
    } else {
      setMakes(formatOptions(makeResponse, 'makeId', 'makeName'));
    }
  };

  const getModels = async (year: string, make: string) => {
    const modelResponse = await getModel(year, make);
    if ('error' in modelResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: modelResponse,
      });
    } else {
      setModels(formatOptions(modelResponse, 'modelName', 'modelName'));
    }
  };

  const handleInputChange = async <K extends keyof ManualLicensePlateForm>(field: K, value: ManualLicensePlateForm[K]) => {
    handleChange(field, value);
    updateManualLicensePlateInfoFormData({ [field]: value });
    if (field === 'year') {
      updateManualLicensePlateInfoFormData({ make: '', model: '' });
      setMakes([]);
      setModels([]);
      await getMakes(value.toString());
    } else if (field === 'make') {
      setModels([]);
      const option = makes.find((opt) => opt.value === value);
      updateManualLicensePlateInfoFormData({ model: '', makeName: option?.label });
      await getModels(manualLicensePlateInfoForm.year, value.toString());
    }
  };

  const handleReEnterPlate = () => {
    trackClick('CTA Clicked', {
      cta_id: 'vehicle_re_enter_plate',
      cta_text: 'RE-ENTER PLATE',
      cta_type: 'secondary_button',
      cta_context: 'vehicle_lookup',
    });
    handleStep(Screens.ENTER_LICENSE_PLATE);
  };

  return (
    <div>
      <form onSubmit={handleSubmit} onFocus={startInteraction}>
        <h1 className="iLendingCDX-form-title">{t('vehicleManualEnter.title')}</h1>

        <Button
          variant="secondary"
          size="lg"
          className={'iLendingCDX-mb-4 iLendingCDX-button--rounded  iLendingCDX-re-enter--buton '}
          onClick={handleReEnterPlate}
        >
          {t('vehicleManualEnter.reEnterPlate')}
        </Button>
        <h2 className="iLendingCDX-form-title--small iLendingCDX-mb-4 ">{t('vehicleManualEnter.tryAgain')}</h2>
        <div>
          <CustomSelect
            name="year"
            label={t('vehicleManualEnter.form.year')}
            className={'iLendingCDX-mb-4'}
            options={years}
            value={manualLicensePlateInfoForm.year}
            errorMessage={errors.year}
            disabled={years.length === 0}
            onChange={(e) => handleInputChange('year', e.target.value)}
          />

          <CustomSelect
            name="make"
            label={t('vehicleManualEnter.form.make')}
            className={'iLendingCDX-mb-6'}
            options={makes}
            value={manualLicensePlateInfoForm.make}
            errorMessage={errors.make}
            disabled={!manualLicensePlateInfoForm.year || makes.length === 0}
            onChange={(e) => handleInputChange('make', e.target.value)}
          />
          <CustomSelect
            name="model"
            label={t('vehicleManualEnter.form.model')}
            className={'iLendingCDX-mb-6'}
            options={models}
            value={manualLicensePlateInfoForm.model}
            errorMessage={errors.model}
            disabled={!manualLicensePlateInfoForm.year || !manualLicensePlateInfoForm.make || models.length === 0}
            onChange={(e) => handleInputChange('model', e.target.value)}
          />
          <CustomSelect
            name="estimatedMileage"
            label={t('vehicleManualEnter.form.selectEstimatedMileage')}
            className={'iLendingCDX-mb-6'}
            options={mileages}
            placeholder={t('vehicleManualEnter.form.selectEstimatedMileage')}
            value={manualLicensePlateInfoForm.estimatedMileage}
            errorMessage={errors.estimatedMileage}
            onChange={(e) => handleInputChange('estimatedMileage', e.target.value)}
          />
          <PriceInput
            name="currentMonthlyPayment"
            label={t('vehicleManualEnter.form.currentMonthlyPayment')}
            className={'iLendingCDX-mb-6'}
            value={manualLicensePlateInfoForm.currentMonthlyPayment}
            errorMessage={errors.currentMonthlyPayment}
            onChange={(value) =>
              handleInputChange('currentMonthlyPayment', value as ManualLicensePlateForm['currentMonthlyPayment'])
            }
          />

          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
          >
            {t('vehicleManualEnter.form.continue')}
          </Button>
        </div>
      </form>
    </div>
  );
};
export default VehicleManualEnterForm;
